import React from 'react';
import type { PluginModule, NavigationItem } from '../../plugins/types';
import BillValidationLayout from './layout/BillValidationLayout';
import { BillValidationDashboard } from './pages/BillValidationDashboard';
import { NEMValidatorPage } from './pages/NEMValidatorPage';
import { OrganisationListPage } from './pages/OrganisationListPage';
import { OrganisationDetailPage } from './pages/OrganisationDetailPage';
import { OrganisationFormPage } from './pages/OrganisationFormPage';
import { SiteListPage } from './pages/SiteListPage';
import { SiteDetailPage } from './pages/SiteDetailPage';
import { SiteFormPage } from './pages/SiteFormPage';
import { AttachmentsPage } from './pages/AttachmentsPage';
import { AdminPage } from './pages/AdminPage';
import { ContactsListPage } from './pages/ContactsListPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { BillsListPage } from './pages/BillsListPage';
import { BillDetailPage } from './pages/BillDetailPage';
import { BillValidationReportsPage } from './pages/BillValidationReportsPage';
import { ReportPrintPage } from './pages/ReportPrintPage';
import { BulkBillRunPage } from './pages/BulkBillRunPage';
import { BillRunsPage } from './pages/BillRunsPage';
import { BvEmailTemplatesPage } from './pages/BvEmailTemplatesPage';
import { ExportPage } from './pages/ExportPage';
import { InboxPage } from './pages/InboxPage';
import { TariffUsageReportPage } from './pages/TariffUsageReportPage';
import { ReportsHubPage } from './pages/ReportsHubPage';
import { IntervalExportPage } from './pages/IntervalExportPage';
import { NtaPage } from './pages/NtaPage';
import { NtaResultPage } from './pages/NtaResultPage';
import { NtaReportPage } from './pages/NtaReportPage';
import { JobsPage } from './pages/JobsPage';

const withLayout = (element: React.ReactNode) =>
  React.createElement(BillValidationLayout, null, element);

const navigation: NavigationItem[] = [
  {
    name: 'Bill Validation',
    route: '/bill-validation',
    icon: 'FileCheck',
    order: 9,
    permissions: ['bill_validation.view'],
  },
];

const billValidationPlugin: PluginModule = {
  routes: [
    {
      path: '/bill-validation',
      element: withLayout(React.createElement(BillValidationDashboard)),
    },
    {
      path: '/bill-validation/nem-validator',
      element: withLayout(React.createElement(NEMValidatorPage)),
    },
    {
      path: '/bill-validation/organisations',
      element: withLayout(React.createElement(OrganisationListPage)),
    },
    {
      path: '/bill-validation/organisations/new',
      element: withLayout(React.createElement(OrganisationFormPage)),
    },
    {
      path: '/bill-validation/organisations/:id',
      element: withLayout(React.createElement(OrganisationDetailPage)),
    },
    {
      path: '/bill-validation/organisations/:id/edit',
      element: withLayout(React.createElement(OrganisationFormPage)),
    },
    {
      path: '/bill-validation/sites',
      element: withLayout(React.createElement(SiteListPage)),
    },
    {
      path: '/bill-validation/sites/new',
      element: withLayout(React.createElement(SiteFormPage)),
    },
    {
      path: '/bill-validation/sites/:id',
      element: withLayout(React.createElement(SiteDetailPage)),
    },
    {
      path: '/bill-validation/sites/:id/edit',
      element: withLayout(React.createElement(SiteFormPage)),
    },
    {
      path: '/bill-validation/contacts',
      element: withLayout(React.createElement(ContactsListPage)),
    },
    {
      path: '/bill-validation/contacts/:id',
      element: withLayout(React.createElement(ContactDetailPage)),
    },
    {
      path: '/bill-validation/inbox',
      element: withLayout(React.createElement(InboxPage)),
    },
    {
      path: '/bill-validation/bills',
      element: withLayout(React.createElement(BillsListPage)),
    },
    {
      path: '/bill-validation/bills/:id',
      element: withLayout(React.createElement(BillDetailPage)),
    },
    // Parse Status used to live here (job 75). It is now a tab in the Super
    // Admin portal — the failures it lists are acted on by editing a retailer
    // parsing prompt, which is super-admin-only, so the page moved to sit with
    // that control. The component still lives under this feature.
    {
      path: '/bill-validation/bill-runs',
      element: withLayout(React.createElement(BulkBillRunPage)),
    },
    // Bill Runs: one page for bills, runs and reports (tasks/prd/prd-bill-runs.md).
    // Runs beside the three pages it will replace until the business signs off.
    {
      path: '/bill-validation/runs',
      element: withLayout(React.createElement(BillRunsPage)),
    },
    // Bulk Report. The path stays /reports so existing links keep working.
    {
      path: '/bill-validation/reports',
      element: withLayout(React.createElement(BillValidationReportsPage)),
    },
    // Reports tab. Not under /reports, which is the Bulk Customer Report.
    {
      path: '/bill-validation/report-hub',
      element: withLayout(React.createElement(ReportsHubPage)),
    },
    {
      path: '/bill-validation/report-hub/tariff-usage',
      element: withLayout(React.createElement(TariffUsageReportPage)),
    },
    {
      path: '/bill-validation/report-hub/interval-export',
      element: withLayout(React.createElement(IntervalExportPage)),
    },
    // Network Tariff Analysis (docs/superpowers/plans/2026-10-24-nta-engine.md).
    {
      path: '/bill-validation/nta',
      element: withLayout(React.createElement(NtaPage)),
    },
    // Client report preview — no layout, so the A4 pages print clean.
    {
      path: '/bill-validation/nta/report',
      element: React.createElement(NtaReportPage),
    },
    {
      path: '/bill-validation/nta/:resultId',
      element: withLayout(React.createElement(NtaResultPage)),
    },
    // Headless target for the background bulk send worker — no layout, not in the nav.
    {
      path: '/bill-validation/report-print',
      element: React.createElement(ReportPrintPage),
    },
    {
      path: '/bill-validation/email-templates',
      element: withLayout(React.createElement(BvEmailTemplatesPage)),
    },
    {
      path: '/bill-validation/attachments',
      element: withLayout(React.createElement(AttachmentsPage)),
    },
    {
      path: '/bill-validation/admin/jobs',
      element: withLayout(React.createElement(JobsPage)),
    },
    {
      path: '/bill-validation/admin',
      element: withLayout(React.createElement(AdminPage)),
    },
    {
      path: '/bill-validation/export',
      element: withLayout(React.createElement(ExportPage)),
    },
  ],
  navigation,
  providers: [],
  config: {
    name: 'billValidation',
    version: '0.2.0',
    description: 'Energy bill upload, validation and anomaly detection',
  },
  initialize: async () => {
    console.log('📋 Bill Validation plugin initialized');
  },
  cleanup: async () => {
    console.log('📋 Bill Validation plugin cleaned up');
  },
};

export default billValidationPlugin;
